// Pure logic for the Queue's "To Address" dropdown grouping (App.jsx's
// ConversationList) — extracted so it can be unit-tested with node --test,
// the same convention toAddressAutoSelect.js uses.
//
// The directory comes from GET /api/conversations/m365-addresses (the
// server's m365AddressDirectory()). Each entry names its owning mailbox's
// primary address (`mailbox`) and whether it is an alias (`isAlias`). The
// dropdown shows one group per mailbox: the primary address, with its aliases
// under it (collapsed by default). The filter state itself stays a flat list
// of addresses; these helpers only compute the next list for a group or an
// address toggle.

// One group per primary address, in the directory's order, each with its
// aliases in registry order: [{ primary, aliases: [address, ...] }]. An entry
// without `mailbox` (an older server) is its own group, and so is an alias
// whose mailbox never appeared, so no address is ever dropped.
export function groupToAddressDirectory(directory) {
  const groups = []
  const byMailbox = new Map()
  for (const entry of directory ?? []) {
    const key = String(entry.mailbox ?? entry.address).toLowerCase()
    const group = byMailbox.get(key)
    if (entry.isAlias && group) {
      group.aliases.push(entry.address)
      continue
    }
    const next = { primary: entry.address, aliases: [] }
    groups.push(next)
    if (!entry.isAlias && !group) byMailbox.set(key, next)
  }
  return groups
}

// Every address in a group, primary first.
export function groupAddresses(group) {
  return [group.primary, ...group.aliases]
}

// The group checkbox: checked when every address is included, indeterminate
// when some are, unchecked when none are.
export function groupCheckState(group, included) {
  const set = new Set(included ?? [])
  const addresses = groupAddresses(group)
  const count = addresses.filter((a) => set.has(a)).length
  return { checked: count === addresses.length, indeterminate: count > 0 && count < addresses.length }
}

// The next included list when the group checkbox is clicked: a fully included
// group is removed whole; otherwise every address it is missing is added.
export function toggleGroup(group, included) {
  const current = included ?? []
  const addresses = groupAddresses(group)
  if (groupCheckState(group, current).checked) {
    const remove = new Set(addresses)
    return current.filter((a) => !remove.has(a))
  }
  const set = new Set(current)
  return [...current, ...addresses.filter((a) => !set.has(a))]
}

// The next included list when one address's own checkbox is clicked.
export function toggleAddress(address, included) {
  const current = included ?? []
  return current.includes(address) ? current.filter((a) => a !== address) : [...current, address]
}
